import { useState, useEffect } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { NAV_LINKS, BUSINESS } from "../lib/data";
import { useContact } from "../contexts/ContactContext";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openGate } = useContact();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid="main-navbar"
      className={`sticky top-0 z-40 w-full bg-white/95 backdrop-blur-sm border-b transition-all duration-200 ${
        scrolled ? "shadow-md border-gray-200 py-1" : "border-transparent py-2"
      }`}
    >
      <div className="msc-container flex items-center justify-between h-16 sm:h-20">
        {/* Brand */}
        <a
          href="#home"
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-[#1E3A8A] flex items-center justify-center shadow-md">
            <span className="font-display font-black text-white text-lg leading-none">
              MS
            </span>
          </div>
          <div className="leading-tight">
            <div className="font-display font-black text-base sm:text-lg text-[#1F2937]">
              Mehdi Soap Center
            </div>
            <div className="text-[11px] sm:text-xs font-semibold tracking-wider text-[#D97706] uppercase">
              Since 1971
            </div>
          </div>
        </a>

        {/* Desktop links */}
        <ul className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-bold text-[#4B5563] hover:text-[#1E3A8A] transition-colors"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        {/* Desktop CTAs */}
        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={() => openGate()}
            data-testid="nav-contact-btn"
            className="btn-primary !px-5 !py-2 text-sm"
          >
            <MessageCircle className="w-4 h-4" /> Contact Us
          </button>
        </div>

        {/* Mobile menu */}
        <button
          aria-label="Open menu"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-md border border-gray-200 text-[#1F2937] active:bg-gray-50"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <div className="lg:hidden border-t border-gray-200 bg-white shadow-xl" data-testid="mobile-menu">
          <ul className="msc-container py-3 flex flex-col">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="block py-3 text-base font-bold text-[#1F2937] hover:text-[#1E3A8A] border-b border-gray-100 last:border-b-0"
                >
                  {l.label}
                </a>
              </li>
            ))}
            <div className="flex pt-5 pb-3">
              <button
                onClick={() => {
                  setOpen(false);
                  openGate();
                }}
                data-testid="mobile-contact-btn"
                className="btn-primary flex-1 text-sm"
              >
                <MessageCircle className="w-4 h-4" /> Contact Us
              </button>
            </div>
          </ul>
        </div>
      )}
    </nav>
  );
};
